import logo from './logo.svg';
import './App.css';
import Home from './Layout';
import {BrowserRouter as Router, Route, Routes, useLocation} from 'react-router-dom'
import Layout from './Layout'
import {CssBaseline, ThemeProvider, createTheme} from '@mui/material'
import { Box } from "@mui/material";
import RequireAuth from './components/RequireAuth'  // Modifica questa riga

// Landing & Admin pages
import LandingPage from './pages/landing/LandingPage';
import FeaturesPage from './pages/landing/FeaturesPage';
import PricingPage from './pages/landing/PricingPage';
import AboutPage from './pages/landing/AboutPage';
import DemoRequestPage from './pages/landing/DemoRequestPage';
import SaasDashboard from './pages/admin/SaasDashboard'

// Auth pages
import LoginPage from './pages/LoginPage';
import RegistrationPage from './pages/RegistrationPage'
import NotFoundPage from './pages/NotFoundPage';
import DashboardPage from './pages/DashboardPage'; // Import diretto funzionante
import KioskLoginPage from './pages/KioskLoginPage'; // Login automatico per kiosk TV

// Import lazy components per performance
import {
  DashboardPageLazy,
  TablePageLazy,
  ElderlyDescriptionPageLazy,
  ElderlyAddPageLazy,
  EditElderlyPageLazy,
  SettingsPageLazy,
  CalendarPageLazy,
  DoctorPageLazy,
  ManageDoctorsPageLazy,
  ManageRoomsPageLazy,
  FamilyMembersPageLazy,
  ElderlyEmergencyStatsPageLazy,
  TherapyPageLazy,
  NursePageLazy,
  ShelterPageLazy,
  TvDisplayPageLazy,
  SensorsPageLazy,
  SensorChartPageLazy
} from './components/LazyComponents';

// Import AppointmentsPage
import AppointmentsPage from './pages/AppointmentsPage';

// Import EventsPage
import EventsPage from './pages/EventsPage';

// Import MaintenancePage
import MaintenancePage from './pages/MaintenancePage';

// Import MQTT Settings Page
import MqttSettingsPage from './pages/MqttSettingsPage';

// Import ContactsPage
import ContactsPage from './pages/ContactsPage';
import MedicinesPage from './pages/MedicinesPage';

// Import PrintableForms pages
import DocumentsPage from './pages/PrintableForms/DocumentsPage';
import WeeklyExitFormPage from './pages/PrintableForms/WeeklyExitFormPage';
import WeeklyVisitFormPage from './pages/PrintableForms/WeeklyVisitFormPage';

import PersistLogin from './components/PersistLogin';
import SensorThChartPage from "./pages/SensorThChartPage";
import TestUpload from "./pages/TestUpload"; // Test upload page
import AccountPage from "./pages/AccountPage"; // Account page
import TvLauncherPage from "./pages/TvLauncherPage";
import TvDisplayPageLegacy from './pages/TvDisplayPageLegacy';
import SmartsuppWidget from './components/SmartsuppWidget'; // Widget chat per pagine pubbliche
import { useState, useEffect, useRef } from 'react';
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar } from '@mui/material';
import axios from './api/axios';
import io from 'socket.io-client';
import { Navigate } from 'react-router-dom';
import useAuth from './hooks/useAuth';

// Importa il provider di tema
import { ColorModeContextProvider } from './contexts/ColorModeContext';

// Importa i componenti per la gestione errori
import ErrorBoundary from './components/ErrorBoundary';
import { GlobalNotificationProvider } from './components/GlobalNotificationProvider';

// WebViewTest.js
const WebViewTest = () => {
  return (
    <div style={{padding: 20, background: 'white', color: 'black'}}>
      <h1>WebView Test Page</h1>
      <div id="status">Se vedi questo testo, la WebView funziona.</div>
      <div id="userAgent">{navigator.userAgent}</div>
    </div>
  );
};

// Funzione per rilevare WebView
const isWebViewAndroid7 = () => {
  const ua = navigator.userAgent.toLowerCase();
  return (
    ua.indexOf('android 7') !== -1 && 
    (ua.indexOf('wv') !== -1 || ua.indexOf('webview') !== -1)
  );
};

// Componente per reindirizzare l'index in base al tipo di utente
const DashboardRedirect = () => {
  const { auth } = useAuth();
  const BASE_PATH = process.env.REACT_APP_BASE_PATH || '/app-anziani';
  const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH || '/admin';
  
  // Se è superadmin, vai alla SaaS dashboard
  if (auth?.userType === 'superadmin') {
    return <Navigate to={`${ADMIN_PATH}/dashboard`} replace />;
  }
  
  // Altrimenti mostra la dashboard normale
  return <DashboardPage />;
};

function App() {
  // Percorsi da .env
  const BASE_PATH = process.env.REACT_APP_BASE_PATH || '/app-anziani'; // Gestionale Anziani
  const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH || '/admin';      // Dashboard SaaS Admin
  const LANDING_PATH = process.env.REACT_APP_LANDING_PATH || '/';       // Landing Page
  
  return (
      <ErrorBoundary 
      showDetails={process.env.NODE_ENV === 'development'}
      onError={(error, errorInfo) => {
        // Log per servizi di monitoraggio esterni
        console.error('App Error Boundary:', error, errorInfo);
      }}
    >
      <ColorModeContextProvider>
        {(themeMode, toggleTheme, theme) => (
          <GlobalNotificationProvider
            position={{ vertical: 'bottom', horizontal: 'left' }}
            variant="filled"
            maxVisible={3}
          >
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <Router>
                {/* Widget Smartsupp - solo per utenti NON autenticati */}
                <SmartsuppWidget />
                
                <Routes>
                  {/* ========== LANDING PAGE ========== */}
                  <Route path={LANDING_PATH} element={<LandingPage />} />
                  <Route path="/features" element={<FeaturesPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/demo" element={<DemoRequestPage />} />
                  
                  {/* ========== AUTH PAGES (pubbliche) ========== */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegistrationPage />} />
                  <Route path="/tv-launcher" element={<TvLauncherPage />} />
                  <Route path="/kiosk-login" element={<KioskLoginPage />} />
                  
                  {/* ========== PROTECTED ROUTES ========== */}
                  <Route element={<PersistLogin />}>
                    <Route element={<RequireAuth/>}>
                      
                      {/* ========== ADMIN SAAS DASHBOARD (standalone, no layout) ========== */}
                      <Route path={`${ADMIN_PATH}/dashboard`} element={<SaasDashboard />} />
                      
                      {/* ========== GESTIONALE ANZIANI (con Layout) ========== */}
                      <Route path={BASE_PATH} element={<Layout/>}>
                        <Route index element={<DashboardRedirect/>}></Route>
                        <Route path="settings" element={<SettingsPageLazy/>}></Route>
                        <Route path="mqtt-settings" element={<MqttSettingsPage/>}></Route>
                        <Route path="elderly" element={<TablePageLazy/>}></Route>
                        <Route path="adduser" element={<ElderlyAddPageLazy/>}></Route>
                        <Route path="elderly/add" element={<ElderlyAddPageLazy/>}></Route>
                        <Route path="elderly/:id" element={<ElderlyDescriptionPageLazy/>}></Route>
                        <Route path="elderly/edit/:id" element={<EditElderlyPageLazy />} />
                        <Route path="elderly/doctor/:id" element={<DoctorPageLazy />} />
                        <Route path="doctors" element={<ManageDoctorsPageLazy />} />
                        <Route path="rooms" element={<ManageRoomsPageLazy />} />
                        <Route path="elderly/:id/family-members" element={<FamilyMembersPageLazy />} />
                        <Route path="elderly/:id/emergency-stats" element={<ElderlyEmergencyStatsPageLazy />} />
                        <Route path="therapy/:elderlyId" element={<TherapyPageLazy />} />
                        <Route path="shelter/:elderlyId" element={<ShelterPageLazy />} />
                        <Route path="sensors/:sensorId" element={<SensorChartPageLazy />} />
                        <Route path="calendar" element={<CalendarPageLazy />} />
                        <Route path="sensors" element={<SensorsPageLazy />} />
                        <Route path="nurses" element={<NursePageLazy />} />
                        <Route path="account" element={<AccountPage />} />
                        <Route path="appointments" element={<AppointmentsPage />} />
                        <Route path="elderly/:id/appointments" element={<AppointmentsPage />} />
                        <Route path="events" element={<EventsPage />} />
                        <Route path="maintenance" element={<MaintenancePage />} />
                        <Route path="contacts" element={<ContactsPage />} />
                        <Route path="medicines" element={<MedicinesPage />} />
                        <Route path="test-upload" element={<TestUpload />} />
                        
                        {/* Documenti stampabili */}
                        <Route path="documents" element={<DocumentsPage />} />
                        <Route path="documents/weekly-exits" element={<WeeklyExitFormPage />} />
                        <Route path="documents/weekly-visits" element={<WeeklyVisitFormPage />} />
                      </Route>
                    </Route>
                  </Route>

                  {/* Altre pagine standalone - sotto BASE_PATH */}
                  <Route element={<RequireAuth />}>
                    <Route 
                      path={`${BASE_PATH}/tv-display`}
                      element={isWebViewAndroid7() ? <TvDisplayPageLegacy /> : <TvDisplayPageLazy />}
                    />
                  </Route>
                  <Route path={`${BASE_PATH}/webview-test`} element={<WebViewTest />} />
                  <Route path="*" element={<NotFoundPage/>}></Route>
                </Routes>
              </Router>
              <div className="App">
                <div id="dialog-root" /> {/* Punto di mount dedicato per dialoghi */}
              </div>
            </ThemeProvider>
          </GlobalNotificationProvider>
        )}
      </ColorModeContextProvider>
    </ErrorBoundary>
  );
}

export default App;
